import type { UIGameState } from '@sorrem-client/types';
import { Button, ItemTransferProvider, Notification } from '@sorrem-client/ui/components';
import { HudAreaFrame, HudAreaSlot, hasOtherRosterMembers, PartyTabs, RunPartyPanel, usePartyChatTab, type PartyLogTab } from '@sorrem-client/ui/features/Hud';
import { useRunHudPanels, useRunItemTransfer } from '@sorrem-client/ui/features/GameOverlay/hooks';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useGameSelector, useHudPanels } from '@sorrem-client/ui/hooks';
import { useMemo, type ReactNode } from 'react';
import { CombatLogPanel } from '../CombatLogPanel';
import { CommissionPanel } from '../CommissionPanel';
import { ExpelledOverlay } from '../ExpelledOverlay';
import { GameOverlayAreaHead } from '../GameOverlayAreaHead';
import { GameOverlayRunEnd } from '../GameOverlayRunEnd';
import { GameOverlayTargetEssence } from '../GameOverlayTargetEssence';
import { MiniMapPanel } from '../MiniMapPanel';
import { YouDiedOverlay } from '../YouDiedOverlay';
import { GameOverlayInventory } from './GameOverlayInventory';
import styles from './GameOverlayHud.module.scss';
import { isDown } from '@sorrem/shared';
import { isNightAt } from '@sorrem-client/game/game-clock';

/** The local player's fall: the choice while they're down, then what they kept if they were cast out. */
function GameOverlayDeathState(): JSX.Element | null {
    const { bridge, requestFallenChoice } = useGameplay();
    const isLocalPlayerDown = useGameSelector(bridge, (state) => isDown(state.player));
    const fallen = useGameSelector(bridge, (state) => state.fallen);
    const expelled = useGameSelector(bridge, (state) => state.expelled);
    const hasRunEnded = useGameSelector(bridge, (state) => state.runResult !== null);

    if (expelled !== null) {
        return <ExpelledOverlay expelled={expelled} />;
    }
    if (hasRunEnded || !isLocalPlayerDown || fallen === null) {
        return null;
    }
    return <YouDiedOverlay fallen={fallen} onChoose={requestFallenChoice} />;
}

interface GameOverlayPartyProps {
    readonly leaderAccountId: string | null;
}

const NO_ROSTER: NonNullable<UIGameState['crucibleSessionState']>['roster'] = [];

/**
 * The party HUD area: the Party, Chat and Log tabs. It reads the party and roster itself, so their changes
 * re-render only this panel. A solo run has nobody to list or talk to, so only the Log tab (#294, #273).
 */
function GameOverlayParty({ leaderAccountId }: GameOverlayPartyProps): JSX.Element {
    const { bridge } = useGameplay();
    // Read from the crucible page's chat provider, so a message re-renders this panel alone.
    const chat = usePartyChatTab();
    const party = useGameSelector(bridge, (state) => state.party);
    const roster = useGameSelector(bridge, (state) => state.crucibleSessionState?.roster ?? NO_ROSTER);
    // Shown or hidden as the account last left it, from one run to the next.
    const { panels, setPanelOpen } = useHudPanels();
    const isLogOpen = panels.combatLog;
    const log = useMemo((): PartyLogTab => ({
        content: <CombatLogPanel />,
        isOpen: isLogOpen,
        onOpenChange: (open) => setPanelOpen('combatLog', open),
    }), [isLogOpen, setPanelOpen]);
    const hasPartyMembers = hasOtherRosterMembers(roster);
    // A fallen member (who can still be helped) flashes the Party tab while another tab shows.
    const hasFallenPartyMember = party.some((member) => !member.isLocal && !member.isDisconnected && isDown(member));
    return (
        <PartyTabs
            party={hasPartyMembers ? <RunPartyPanel roster={roster} party={party} leaderAccountId={leaderAccountId} /> : null}
            chat={hasPartyMembers ? chat : null}
            log={log}
            isPartyAttentionNeeded={hasFallenPartyMember}
        />
    );
}

/** The commission HUD area, when the run was provisioned for one. It reads the commission itself, so its progress re-renders only this panel. */
function GameOverlayCommission({ hasRunEnded, open, onOpenChange }: {
    readonly hasRunEnded: boolean;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
}): JSX.Element | null {
    const { bridge } = useGameplay();
    const commission = useGameSelector(bridge, (state) => state.commission);
    if (commission === null || hasRunEnded) {
        return null;
    }
    return (
        <HudAreaSlot panel="commission">
            <CommissionPanel commission={commission} open={open} onOpenChange={onOpenChange} />
        </HudAreaSlot>
    );
}

/** Moving items in the run, and their right-click menu. Reads the carried and equipped items itself, so a pickup re-renders only the inventory. */
function GameOverlayItemTransfer({ children }: { readonly children: ReactNode }): JSX.Element {
    const { onMoveTo, canMoveTo, itemMenu } = useRunItemTransfer();
    return <ItemTransferProvider onMoveTo={onMoveTo} canMoveTo={canMoveTo} itemMenu={itemMenu}>{children}</ItemTransferProvider>;
}

/** Darkens the HUD at night. Re-renders only when night falls or lifts, not on every game-time update. */
function GameOverlayNightVignette(): JSX.Element | null {
    const { bridge } = useGameplay();
    const isNight = useGameSelector(bridge, (state) => isNightAt(state.gameTimeSec));
    return isNight ? <div className={styles.nightVignette} /> : null;
}

interface GameOverlayHudProps {
    /** Whether the run has a crucible session to show the party of. */
    readonly hasCrucibleSession: boolean;
    readonly partyLeaderAccountId: string | null;
    readonly hasRunEnded: boolean;
    /** Why quitting the run failed, or null. */
    readonly quitError: string | null;
    readonly onDismissQuitError: () => void;
    /** Reports whether the carried panel or loadout has something for Esc to dismiss before the run menu. */
    readonly onInventoryDismissableChange: (hasDismissable: boolean) => void;
}

/** The run's HUD, once it is under way: the HUD areas' panels, the loadout, and how the run ends. */
export function GameOverlayHud({
    hasCrucibleSession,
    partyLeaderAccountId,
    hasRunEnded,
    quitError,
    onDismissQuitError,
    onInventoryDismissableChange,
}: GameOverlayHudProps): JSX.Element {
    // Called once: it also binds the map and commission hotkeys.
    const { isMapOpen, setMapOpen, isCommissionOpen, setCommissionOpen } = useRunHudPanels();

    return (
        <>
            <GameOverlayNightVignette />

            <div className={styles.wrapper}>
                <HudAreaFrame topRightHead={<GameOverlayAreaHead />}>
                    {hasCrucibleSession && !hasRunEnded && (
                        <HudAreaSlot panel="party">
                            <GameOverlayParty leaderAccountId={partyLeaderAccountId} />
                        </HudAreaSlot>
                    )}
                    <HudAreaSlot panel="map">
                        <MiniMapPanel open={isMapOpen} onOpenChange={setMapOpen} />
                    </HudAreaSlot>
                    <GameOverlayCommission hasRunEnded={hasRunEnded} open={isCommissionOpen} onOpenChange={setCommissionOpen} />

                    {!hasRunEnded && <GameOverlayTargetEssence />}

                    <GameOverlayDeathState />

                    {quitError !== null && !hasRunEnded && (
                        <Notification
                            title="Unable to Quit"
                            message={quitError}
                        >
                            <Button variant="secondary" onClick={onDismissQuitError}>Dismiss</Button>
                        </Notification>
                    )}

                    <GameOverlayRunEnd />

                    {/* Carried panel (in its HUD area); bottom-center: loadout */}
                    <GameOverlayItemTransfer>
                        <GameOverlayInventory onDismissableChange={onInventoryDismissableChange} />
                    </GameOverlayItemTransfer>

                </HudAreaFrame>
            </div>
        </>
    );
}
